/**
 * Results dashboard (FR-14, FR-15).
 *
 * Summary first, then one expandable row per check. Failing checks open by
 * default: the reason someone ran this is to see what is wrong, and making them
 * click to find out would be a poor trade.
 */

import { useMemo, useState } from 'react';
import { ApiError } from '../lib/api';
import type { CheckResult, Finding, ValidationReport } from '../lib/types';
import {
  Badge,
  ChevronIcon,
  DownloadIcon,
  MailIcon,
  ShieldIcon,
  StatusBadge,
  VERDICT_LABEL,
  formatBytes,
  formatDateTime,
  formatDuration,
  formatNumber,
} from './primitives';

interface EmailReportInput {
  recipientEmail: string;
  message: string;
  includeBcf: boolean;
}

interface ReportViewProps {
  report: ValidationReport;
  onDownload?: (format: 'pdf' | 'csv' | 'bcf') => void;
  downloading?: 'pdf' | 'csv' | 'bcf' | null;
  watermarked?: boolean;
  /** Present only when sending a report by email is available (Professional+).
   * Rejects (throwing ApiError where relevant) on failure -- the form shows
   * whatever message comes back. */
  onEmail?: (input: EmailReportInput) => Promise<void>;
}

export function ReportView({
  report,
  onDownload,
  downloading,
  watermarked,
  onEmail,
}: ReportViewProps) {
  return (
    <div className="stack">
      <VerdictPanel report={report} />

      {report.fatal_error && (
        <div className="callout callout-error" role="alert">
          <div>
            <strong>The file could not be validated.</strong>
            <div style={{ marginTop: '0.2rem' }}>{report.fatal_error}</div>
          </div>
        </div>
      )}

      {onDownload && (
        <div className="card">
          <div className="card-body tight row">
            <ShieldIcon />
            <span className="hint-strong">
              Report ready. The uploaded model has already been deleted from the server.
            </span>
            <div className="masthead-spacer" />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => onDownload('pdf')}
              disabled={downloading === 'pdf'}
            >
              <DownloadIcon />
              {downloading === 'pdf' ? 'Preparing…' : 'PDF report'}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onDownload('csv')}
              disabled={downloading === 'csv'}
            >
              <DownloadIcon />
              {downloading === 'csv' ? 'Preparing…' : 'CSV export'}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onDownload('bcf')}
              disabled={downloading === 'bcf'}
              title="One BCF issue per failed check, openable directly in Revit, Navisworks, Solibri, or BIMcollab."
            >
              <DownloadIcon />
              {downloading === 'bcf' ? 'Preparing…' : 'BCF issues'}
            </button>
          </div>
          {watermarked && (
            <div className="card-body tight">
              <span className="hint">
                Free-tier PDF reports carry a ClearIFC watermark. The Professional tier
                removes it and unlocks IDS validation.
              </span>
            </div>
          )}
          {onEmail && <EmailReportForm report={report} onEmail={onEmail} />}
        </div>
      )}

      {report.ids_lock && <LockedRuleSetPanel lock={report.ids_lock} />}

      <div className="grid grid-2-1">
        <div>
          <h2 style={{ fontSize: '1.05rem', marginBottom: '0.7rem' }}>Checks</h2>
          {report.checks.map((check) => (
            <CheckCard key={check.check_id} check={check} />
          ))}
        </div>
        <ModelFacts report={report} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- email
function EmailReportForm({
  report,
  onEmail,
}: {
  report: ValidationReport;
  onEmail: (input: EmailReportInput) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [includeBcf, setIncludeBcf] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // The BCF option only makes sense to offer when there's something to
  // flag -- a clean pass has no findings, so a BCF export of it would just
  // be an empty archive.
  const hasFailures = report.counts.failed > 0 || report.counts.warnings > 0;

  async function send() {
    setSending(true);
    setError(null);
    setSentTo(null);
    try {
      await onEmail({ recipientEmail: recipientEmail.trim(), message: message.trim(), includeBcf });
      setSentTo(recipientEmail.trim());
      setMessage('');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The report could not be sent.');
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <div className="card-body tight">
        <button type="button" className="btn btn-sm" onClick={() => setOpen(true)}>
          <MailIcon />
          Email this report
        </button>
      </div>
    );
  }

  return (
    <div className="card-body stack tight">
      <div className="form-grid cols-2">
        <label className="field">
          <span>Send to</span>
          <input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="client@example.com"
          />
        </label>
        {hasFailures && (
          <label className="checkbox" style={{ alignSelf: 'end' }}>
            <input
              type="checkbox"
              checked={includeBcf}
              onChange={(e) => setIncludeBcf(e.target.checked)}
            />
            Include BCF issues (one per failed check)
          </label>
        )}
      </div>
      <label className="field">
        <span>Note (optional)</span>
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Can you fix the fire rating failures before Friday's submission?"
        />
      </label>
      {error && <div className="callout callout-error">{error}</div>}
      {sentTo && !error && (
        <div className="callout callout-info">Report sent to {sentTo}.</div>
      )}
      <div className="row">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void send()}
          disabled={sending || !recipientEmail.trim()}
        >
          <MailIcon />
          {sending ? 'Sending…' : 'Send'}
        </button>
        <button type="button" className="btn btn-sm" onClick={() => setOpen(false)} disabled={sending}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- lock
/** Proof that the IDS behind this report was a signed, unedited .idsx --
 * see app/ids/lock.py. Shown prominently: this is the exact evidence a
 * client asked for when they locked the rule set in the first place. */
function LockedRuleSetPanel({ lock }: { lock: NonNullable<ValidationReport['ids_lock']> }) {
  return (
    <div className="card">
      <div className="card-body tight row">
        <ShieldIcon />
        <span className="hint-strong">
          Validated against a locked, unedited rule set from {lock.locked_by || 'the issuer'}.
        </span>
        <div className="masthead-spacer" />
        <Badge variant="pass">Verified</Badge>
      </div>
      <div className="card-body">
        <div className="meta-list">
          <div className="meta-row">
            <span className="meta-key">Rule set</span>
            <span className="meta-value">{lock.name}</span>
          </div>
          <div className="meta-row">
            <span className="meta-key">Locked by</span>
            <span className="meta-value">{lock.locked_by}</span>
          </div>
          <div className="meta-row">
            <span className="meta-key">Locked at</span>
            <span className="meta-value">{formatDateTime(lock.locked_at)}</span>
          </div>
          {(lock.requested_by_name || lock.requested_by_email) && (
            <div className="meta-row">
              <span className="meta-key">Requested by</span>
              <span className="meta-value">
                {[lock.requested_by_name, lock.requested_by_email].filter(Boolean).join(' — ')}
              </span>
            </div>
          )}
          {lock.project_name && (
            <div className="meta-row">
              <span className="meta-key">Project</span>
              <span className="meta-value">{lock.project_name}</span>
            </div>
          )}
          {lock.comments && (
            <div className="meta-row">
              <span className="meta-key">Comments</span>
              <span className="meta-value">{lock.comments}</span>
            </div>
          )}
          <div className="meta-row">
            <span className="meta-key">SHA-256</span>
            <span className="meta-value cell-mono">{lock.ids_sha256}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- verdict
function VerdictPanel({ report }: { report: ValidationReport }) {
  const { counts } = report;
  return (
    <div className={`verdict verdict-${report.verdict}`}>
      <div>
        <div className="verdict-label">{VERDICT_LABEL[report.verdict]}</div>
        <div className="verdict-file">{report.file.filename}</div>
      </div>
      <div className="verdict-spacer" />
      <div className="verdict-counts">
        <div className="verdict-count">
          <strong>{counts.passed}</strong>
          <span>Passed</span>
        </div>
        <div className="verdict-count">
          <strong>{counts.failed}</strong>
          <span>Failed</span>
        </div>
        <div className="verdict-count">
          <strong>{counts.warnings}</strong>
          <span>Warnings</span>
        </div>
        <div className="verdict-count">
          <strong>{formatNumber(report.total_findings)}</strong>
          <span>Findings</span>
        </div>
      </div>
    </div>
  );
}

function ModelFacts({ report }: { report: ValidationReport }) {
  const { file } = report;
  const rows: Array<[string, string]> = [
    ['Schema', file.schema ?? 'not detected'],
    ['Model view definition', file.mvd ?? 'not declared'],
    ['Authoring application', file.authoring_application ?? 'not declared'],
    ['Project name', file.project_name ?? 'not set'],
    ['Physical elements', formatNumber(file.element_count)],
    ['File size', formatBytes(file.size_bytes)],
    ['Rule template', report.template_name ?? '—'],
    ['IDS specification', report.ids_filename ?? 'none'],
    ['Validation time', formatDuration(report.duration_ms)],
  ];

  return (
    <div className="card">
      <div className="card-head">
        <h3>Model and rule set</h3>
      </div>
      <div className="card-body">
        <div className="meta-list">
          {rows.map(([key, value]) => (
            <div className="meta-row" key={key}>
              <span className="meta-key">{key}</span>
              <span className="meta-value">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- check
function CheckCard({ check }: { check: CheckResult }) {
  const hasDetail = check.findings.length > 0;
  const [open, setOpen] = useState(check.status === 'fail' && hasDetail);

  return (
    <div className="check">
      <button
        type="button"
        className="check-head"
        onClick={() => hasDetail && setOpen((value) => !value)}
        disabled={!hasDetail}
        aria-expanded={hasDetail ? open : undefined}
      >
        {hasDetail ? (
          <ChevronIcon className={`chevron${open ? ' open' : ''}`} />
        ) : (
          <span style={{ width: 16, flex: 'none' }} />
        )}
        <div className="check-grow">
          <div className="check-title">{check.title}</div>
          <div className="check-summary">{check.summary}</div>
        </div>
        {check.failed_count > 0 && (
          <span className="hint">{formatNumber(check.failed_count)} findings</span>
        )}
        <StatusBadge status={check.status} />
      </button>

      {open && hasDetail && (
        <div className="check-body">
          <FindingsTable check={check} />
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ findings
type SortKey = 'severity' | 'ifc_type' | 'name' | 'message';

export function FindingsTable({ check }: { check: CheckResult }) {
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState('all');
  const [type, setType] = useState('all');
  const [sort, setSort] = useState<{ key: SortKey; ascending: boolean }>({
    key: 'severity',
    ascending: true,
  });

  const types = useMemo(() => {
    const found = new Set<string>();
    check.findings.forEach((finding) => {
      if (finding.element?.ifc_type) found.add(finding.element.ifc_type);
    });
    return Array.from(found).sort();
  }, [check.findings]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = check.findings.filter((finding) => {
      if (severity !== 'all' && finding.severity !== severity) return false;
      if (type !== 'all' && finding.element?.ifc_type !== type) return false;
      if (!needle) return true;
      return (
        finding.message.toLowerCase().includes(needle) ||
        (finding.element?.name ?? '').toLowerCase().includes(needle) ||
        (finding.element?.global_id ?? '').toLowerCase().includes(needle) ||
        (finding.element?.ifc_type ?? '').toLowerCase().includes(needle)
      );
    });

    const rank = (finding: Finding) =>
      finding.severity === 'error' ? 0 : finding.severity === 'warning' ? 1 : 2;
    const value = (finding: Finding) => {
      switch (sort.key) {
        case 'severity':
          return rank(finding);
        case 'ifc_type':
          return finding.element?.ifc_type ?? '';
        case 'name':
          return finding.element?.name ?? '';
        default:
          return finding.message;
      }
    };

    return [...filtered].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      const comparison =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
      return sort.ascending ? comparison : -comparison;
    });
  }, [check.findings, query, severity, sort, type]);

  const toggleSort = (key: SortKey) =>
    setSort((current) =>
      current.key === key ? { key, ascending: !current.ascending } : { key, ascending: true },
    );

  const heading = (key: SortKey, label: string) => (
    <th
      className="sortable"
      onClick={() => toggleSort(key)}
      aria-sort={sort.key === key ? (sort.ascending ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sort.key === key ? (sort.ascending ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <>
      <div className="filters">
        <input
          type="text"
          placeholder="Search findings, names or GlobalIds"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search findings"
        />
        <select
          value={severity}
          onChange={(event) => setSeverity(event.target.value)}
          aria-label="Filter by severity"
        >
          <option value="all">All severities</option>
          <option value="error">Errors</option>
          <option value="warning">Warnings</option>
          <option value="info">Information</option>
        </select>
        {types.length > 1 && (
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            aria-label="Filter by IFC type"
          >
            <option value="all">All IFC types</option>
            {types.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
        <span className="hint">
          {formatNumber(rows.length)} of {formatNumber(check.findings.length)} shown
        </span>
      </div>

      <div className="table-wrap">
        <table className="findings-table">
          <thead>
            <tr>
              {heading('severity', 'Severity')}
              {heading('name', 'Element')}
              {heading('ifc_type', 'IFC type')}
              <th>GlobalId</th>
              {heading('message', 'Finding')}
            </tr>
          </thead>
          <tbody>
            {rows.map((finding, index) => (
              <tr key={`${finding.rule_id}-${finding.element?.step_id ?? index}`}>
                <td>
                  <span
                    className={`badge badge-${
                      finding.severity === 'error' ? 'fail' : finding.severity === 'warning' ? 'warning' : 'neutral'
                    }`}
                  >
                    {finding.severity}
                  </span>
                </td>
                <td>{finding.element?.name || '—'}</td>
                <td className="cell-mono">{finding.element?.ifc_type || '—'}</td>
                <td className="cell-mono">{finding.element?.global_id || '—'}</td>
                <td className="cell-message">
                  {finding.message}
                  {finding.fix_guidance && (
                    <details className="fix-guidance">
                      <summary>How to fix this</summary>
                      <p>{finding.fix_guidance.general}</p>
                      {finding.fix_guidance.tool_tip && (
                        <p>
                          <strong>{finding.fix_guidance.tool}:</strong> {finding.fix_guidance.tool_tip}
                        </p>
                      )}
                    </details>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="hint" style={{ padding: '1rem' }}>
                  No findings match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {check.findings_truncated > 0 && (
        <div className="card-body tight">
          <span className="hint">
            {formatNumber(check.findings_truncated)} further findings for this check are not
            listed. Export the CSV for the complete record.
          </span>
        </div>
      )}
    </>
  );
}
